import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import CollapsibleTable from "../../components/collapsible-table";
import Avatar from "@mui/material/Avatar";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import useCompanies from "../../hooks/companies";
import TagColor from "../../components/tag-color";
import UploadButton from "../../components/upload-button";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import AppBar from "@mui/material/AppBar";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import useInspectionQuestions from "../../hooks/inspection-questions";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

const InspectionChecklist = () => {
  const { get: getCompanies, uploadPhoto } = useCompanies();
  const {
    get: getInspectionChecklist,
    create: createQuestion,
    deleteData,
  } = useInspectionQuestions();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [inspectionChecklist, setInspectionChecklist] = useState<
    InspectionQuestion[]
  >([]);
  const [company, setCompany] = useState<Company>();
  const [isLoading, setIsLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<InspectionQuestion>({
    defaultValues: { enabled: true },
  });

  const loadData = useCallback(() => {
    const fn = async () => {
      const resp = await getCompanies();
      setCompanies(resp);
    };
    fn();
  }, []);

  const loadChecklist = useCallback(async () => {
    if (company) {
      const resp = await getInspectionChecklist(company.id);
      setInspectionChecklist(resp);
    }
  }, [company]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);
  const onSubmit: SubmitHandler<InspectionQuestion> = (data) => {
    createQuestion({ ...data, companyId: company?.id });
    loadChecklist();
    toggleDrawer();
    reset();
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleDrawer = () => {
    setOpenDrawer(!openDrawer);
  };
  const handleChange = (event: SelectChangeEvent<string>) => {
    const {
      target: { value },
    } = event;
    setCompany(companies.filter((item) => item.id === value)[0] || undefined);
  };
  const handleDelete = async (questionId: string) => {
    await deleteData(questionId);
    loadChecklist();
  };
  return (
    <div className="pt-[64px] w-full">
      <AppBar
        position="static"
        variant="outlined"
        color="secondary"
        className="flex flex-row"
      >
        <Toolbar>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            Inspection Checklist
          </Typography>
          <FormControl sx={{ m: 1, width: 300 }}>
            <InputLabel id="demo-multiple-name-label">Company</InputLabel>
            <Select
              labelId="demo-multiple-name-label"
              id="demo-multiple-name"
              value={company?.id}
              onChange={handleChange}
              input={<OutlinedInput label="Company" />}
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Toolbar>
      </AppBar>
      {company ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "24px",
            width: "100%",
          }}
        >
          <div className="pb-[24px]">
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={toggleDrawer}
            >
              <AddIcon />
              Add question
            </Button>
          </div>

          <CollapsibleTable
            header={["Question", "Enabled", "", ""]}
            rows={inspectionChecklist.map((question) => {
              return {
                id: question.id,
                cells: [
                  question.question,
                  <Switch disabled defaultChecked={question.enabled} />,
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => null}
                  >
                    Edit
                  </Button>,
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleDelete(question.id)}
                  >
                    Delete
                  </Button>,
                ],
              };
            })}
          />
          <Drawer anchor={"right"} open={openDrawer} onClose={toggleDrawer}>
            <div className="p-[24px] pt-[88px] w-[400px]">
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-[24px]"
              >
                <TextField
                  id="outlined-basic"
                  label="Inspection question"
                  variant="outlined"
                  multiline
                  rows={3}
                  {...register("question", { required: true })}
                />
                <Controller
                  control={control}
                  name="enabled"
                  defaultValue={false}
                  render={() => {
                    return (
                      <FormControlLabel
                        control={
                          <Switch
                            defaultChecked
                            value={watch("enabled")}
                            onChange={(event, val) => {
                              setValue(`enabled`, val);
                            }}
                          />
                        }
                        label="Enabled?"
                      />
                    );
                  }}
                />

                <Button type="submit" variant="contained" color="secondary">
                  Save
                </Button>
              </form>
            </div>
          </Drawer>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "24px",
            width: "100%",
          }}
        >
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            Please select a company
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default InspectionChecklist;
